import type { SurveyConfig } from '@coorum-research/shared-common/types/survey-json.type'
import type { ThemeConfig } from '@coorum-research/shared-common/types/theme.type'
import type { AfterRenderPageEvent, CompleteEvent } from 'survey-core'
import { useMemo } from 'react'
import { QuestionFactory, QuestionTextModel, Serializer, surveyLocalization, SurveyModel } from 'survey-core'
import { coorumCss } from '../survey/coorumCss'
import { coorumTheme } from '../survey/coorumTheme'
import { initSurveySettings } from '../survey/survey-model.util'
import { createSurveyTheme } from '../survey/theme'
import 'survey-core/survey-core.min.css'

type Props = {
  surveyConfig: SurveyConfig | null
  themeJson?: ThemeConfig | null
  resultData?: string | null
  mode?: 'display' | 'edit' // TODO: survey-coreに型があるならそれを使う
  onComplete?: (sender: SurveyModel, option?: CompleteEvent) => void
  onAfterRenderPage?: (sender: SurveyModel, options?: AfterRenderPageEvent) => void
}

// 評価をrenderingするときにdropdownにならないよう、defaultValueをauto→buttonsにする
const ratingDisplayModeProp = Serializer.findProperty('rating', 'displayMode')
ratingDisplayModeProp.defaultValue = 'buttons'

// date型とnumber型の質問タイプを登録
Serializer.addClass('date', [], undefined, 'text')
Serializer.addClass('number', [], undefined, 'text')

QuestionFactory.Instance.registerQuestion('date', (name) => {
  const q = new QuestionTextModel(name)
  q.inputType = 'date'
  return q
})

QuestionFactory.Instance.registerQuestion('number', (name) => {
  const q = new QuestionTextModel(name)
  q.inputType = 'number'
  return q
})

/**
 * 1質問つづ表示の場合にはuseSurveyModelPerQuestion()を検討してください。
 */
export function useSurveyModel({
  surveyConfig,
  themeJson,
  resultData,
  mode,
  onComplete,
  onAfterRenderPage,
}: Props) {
  const model = useMemo(() => {
    initSurveySettings(Serializer, surveyLocalization)

    const model = new SurveyModel(surveyConfig)
    model.locale = 'ja'

    // page render callback
    if (onAfterRenderPage) {
      model.onAfterRenderPage.add(onAfterRenderPage)
    }

    // 完了時コールバック
    if (onComplete) {
      model.onComplete.add(onComplete)
    }

    // 回答の読み込み
    if (resultData) {
      try {
        model.data = JSON.parse(resultData)
      } catch (e) {
        console.error(e)
      }
    }

    // モードの設定(display = 閲覧のみ, edit = 回答可能)
    model.mode = mode ?? 'edit'

    // 質問ごとにページを分ける
    model.questionsOnPageMode = 'questionPerPage'

    // 質問番号は非表示
    model.showQuestionNumbers = false

    // 進捗バーの表示
    model.showProgressBar = 'aboveHeader'
    // ProgressText(Page 1 of n)は表示しない
    model.onProgressText.add((_sender, options) => {
      options.text = ''
    })

    // 完了ページの表示有無
    model.showCompletedPage = false

    if (themeJson) {
      model.applyTheme(createSurveyTheme(themeJson))
    } else {
      model.applyTheme(coorumTheme)
    }
    model.css = coorumCss

    if (model.mode === 'display') {
      model.css.rating.itemStarDisabled = ''
      model.css.rating.itemStarReadOnly = ''
      model.css.rating.itemDisabled = ''
      model.css.rating.itemReadOnly = ''
      model.css.imagepicker.itemReadOnly = ''
      model.css.file.fileDecorator = 'sv-hidden'
      model.css.file.imageWrapper = `${model.css.file.imageWrapper} !bg-transparent`
    }

    return model
  }, [surveyConfig, themeJson, resultData, mode, onComplete, onAfterRenderPage])

  return {
    model,
  }
}
